import GridView from "components/GridView";
import { GridSchema } from "components/GridView/GridView.types";
import { useMemo, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CourseOrderService } from "services/courseOrder";
import { CourseOrderApplierResult } from "services/courseOrder/models/CourseOrderType";
import useGridStore from "components/GridView/grid.store";
import { useShallow } from "zustand/react/shallow";
import { prepareFilters } from "components/GridView/components/GridFilter";
import { useNotification } from "hooks/useNotification";

type ApplicantsTabProps = {
  courseId: string | number;
};

const ApplicantsTab: React.FC<ApplicantsTabProps> = (props) => {
  const { courseId } = props;
  const { t } = useTranslation("Courses");
  const { showNotification } = useNotification();
  const [data, setData] = useState<CourseOrderApplierResult[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const ready = useRef(false);
  const prevSearchValue = useRef<string>("");
  const prevFilters = useRef<any>(null);

  const { sortState, pagination, setPagination, searchValue, filters, resetGridStore } = useGridStore(
    useShallow((state) => ({
      sortState: state.sortState,
      pagination: state.pagination,
      setPagination: state.setPagination,
      searchValue: state.generalSearch,
      filters: state.filters,
      resetGridStore: state.reset,
    })),
  );

  useEffect(() => {
    ready.current = true;
  }, [resetGridStore]);

  useEffect(() => {
    if (!ready.current) {
      resetGridStore();
      ready.current = true;
      return;
    }

    const fetchData = async () => {
      if (!ready.current) return;

      setLoading(true);
      try {
        const sortArray = sortState && Object.keys(sortState).length > 0
          ? Object.keys(sortState).map((key) => ({
              field: key,
              dir: sortState[key],
            }))
          : [];

        // Check if search or filters changed to reset pagination
        const searchChanged = prevSearchValue.current !== searchValue;
        const filtersChanged = JSON.stringify(prevFilters.current) !== JSON.stringify(filters);
        const shouldResetPagination = !pagination.currentPage || searchChanged || filtersChanged;

        // Update refs
        prevSearchValue.current = searchValue;
        prevFilters.current = filters;

        const response = await CourseOrderService.getAllApplierByCourseId(Number(courseId), {
          pageIndex: shouldResetPagination ? 1 : pagination.currentPage,
          pageSize: 10,
          search: searchValue,
          sort: sortArray,
          filter: prepareFilters(filters),
        });

        if (!response.hasError && response.data) {
          setData(response.data.result || []);
          setTotalCount(response.data.totalCount || 0);

          if (shouldResetPagination) {
            setPagination({
              currentPage: 1,
              totalPages: Math.ceil((response.data.totalCount || 0) / 10),
            });
          } else {
            setPagination({
              currentPage: pagination.currentPage,
              totalPages: Math.ceil((response.data.totalCount || 0) / 10),
            });
          }
        }
      } catch (error) {
        console.error("Error fetching applicants:", error);
        showNotification({
          type: "error",
          description: t("Common:internalServerError"),
        });
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, [courseId, resetGridStore, pagination.currentPage, sortState, searchValue, filters]);

  const applicantsGridSchema = useMemo<GridSchema[]>(
    () => [
      {
        field: "userName",
        displayName: t("appliedUserName"),
        showOnMobile: true,
        sortable: true,
        filterable: true,
      },
      {
        field: "academicDegree",
        displayName: t("degree"),
        sortable: true,
        filterable: true,
      },
      {
        field: "college",
        displayName: t("collage"),
        sortable: true,
        filterable: true,
      },
      {
        field: "specialization",
        displayName: t("specialization"),
        showOnMobile: true,
        sortable: true,
        filterable: true,
      },
      {
        field: "email",
        displayName: t("Common:email"),
        sortable: true,
        filterable: true,
      },
      {
        field: "phoneNumber",
        displayName: t("Common:phoneNumber"),
        sortable: true,
        filterable: true,
        render: (row: CourseOrderApplierResult) => (
          <span dir="ltr" style={{ textAlign: "left" }}>
            {row.phoneNumber}
          </span>
        ),
      },
    ],
    [t],
  );

  return (
    <GridView
      loading={loading}
      viewHeaderProps={{
        singularName: t("applicant"),
        title: t("applicants"),
        totalCount: totalCount,
        hideAddButton: true,
      }}
      gridProps={{
        data: data ?? [],
        gridSchema: applicantsGridSchema,
      }}
    />
  );
};

export default ApplicantsTab;
